import { createContext, useEffect, useState } from "react";
import { fetchDataFromApi } from "../utils/api";
import { loadVideosOnce } from "../firebase";
import videosData from "../data/videos.json";
import { toAppVideo } from "../utils/videoHelper";

// (parseYoutubeId removed because it's not currently used)

// Convert firebase/local entry into the app's video shape
const firebaseToVideo = (item) => {
  const v = toAppVideo(item);
  if (!v) return null;
  return { type: "video", video: v };
};

export const DataContext = createContext();

export const AppContext = (props) => {
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("New");
  // mobile menu removed
  const [theme, setThemeState] = useState(null);
  const [autoTheme, setAutoTheme] = useState(true);
  const [firebaseVideos, setFirebaseVideos] = useState([]);
  const [localVideos, setLocalVideos] = useState([]);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  // simple toast system
  const [toasts, setToasts] = useState([]);
  const addToast = (message, type = 'info', duration = 4000) => {
    try {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      const t = { id, message, type };
      setToasts((s) => [...s, t]);
      setTimeout(() => {
        setToasts((s) => s.filter((x) => x.id !== id));
      }, duration);
    } catch (e) { console.warn('addToast error', e); }
  };
  const removeToast = (id) => setToasts((s) => s.filter((t) => t.id !== id));

  useEffect(() => {
    fetchSelectedCaregoryData(selectedCategory);
  }, [selectedCategory]);

  // load Firebase RTDB /videos once and map to our video shape
  useEffect(() => {
    const loadFirebaseVideos = async () => {
      setFirebaseLoading(true);
      try {
        const val = await loadVideosOnce();
        if (!val) {
          setFirebaseVideos([]);
          setFirebaseLoading(false);
          return;
        }
        const arr = Object.keys(val).map((k) => val[k]);
        const mapped = arr
          .map((it) => firebaseToVideo(it))
          .filter(Boolean)
          .map((v) => v.video); // store just the inner `video` object for convenience
        setFirebaseVideos(mapped);
      } catch (error) {
        console.error('Error loading Firebase videos:', error);
        setFirebaseVideos([]);
      } finally {
        setFirebaseLoading(false);
      }
    };
    loadFirebaseVideos();
  }, []);

  // load local JSON content and convert to app shape
  useEffect(() => {
    try {
      if (Array.isArray(videosData)) {
        const mapped = videosData.map((it) => toAppVideo(it)).filter(Boolean);
        setLocalVideos(mapped);
      } else if (videosData && Array.isArray(videosData.items)) {
        const mapped = videosData.items.map((it) => toAppVideo(it)).filter(Boolean);
        setLocalVideos(mapped);
      }
    } catch (e) {
      setLocalVideos([]);
    }
  }, []);

  const fetchSelectedCaregoryData = async (query) => {
    setLoading(true);
    try {
      const data = await fetchDataFromApi(`search/?q=${query}`);
      // console.log(data.contents); //i did data.contents because the api gives all video data inside contents
      setSearchResults(data?.contents || []);
    } catch (e) {
      console.error("fetchSelectedCaregoryData error:", e);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto theme based on local time (night => dark, day => light)
  const getThemeFromTime = () => {
    try {
      const hour = new Date().getHours();
      // consider night between 19:00 - 05:59
      return hour >= 19 || hour < 6 ? "dark" : "light";
    } catch (e) {
      return "light";
    }
  };

  useEffect(() => {
    // if user previously chose manual theme, respect it
    try {
      const manualFlag = localStorage.getItem("theme_auto");
      const storedTheme = localStorage.getItem("theme");
      if (manualFlag === "false" && (storedTheme === "dark" || storedTheme === "light")) {
        setAutoTheme(false);
        setThemeState(storedTheme);
        return;
      }
    } catch (e) {}

    // otherwise set theme based on time and enable auto mode
    setAutoTheme(true);
    setThemeState(getThemeFromTime());

    // re-evaluate every 15 minutes in case user keeps page open across boundaries
    const id = setInterval(() => {
      setThemeState(getThemeFromTime());
    }, 15 * 60 * 1000);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (theme === "dark") {
      document.getElementById("root").classList.add("dark");
    } else {
      document.getElementById("root").classList.remove("dark");
    }
  }, [theme]);

  // wrapper to expose as `setTheme` in context: records manual preference and disables auto mode
  const setTheme = (value) => {
    try {
      if (value === "dark" || value === "light") {
        setThemeState(value);
        setAutoTheme(false);
        localStorage.setItem("theme", value);
        localStorage.setItem("theme_auto", "false");
      }
    } catch (e) {}
  };

  const enableAutoTheme = () => {
    try {
      setAutoTheme(true);
      localStorage.setItem("theme_auto", "true");
      const t = getThemeFromTime();
      setThemeState(t);
    } catch (e) {}
  };

  return (
    <DataContext.Provider
      value={{
        loading,
        setLoading,
  searchResults,
  firebaseVideos,
  localVideos,
  firebaseLoading,
        selectedCategory,
        setSelectedCategory,
  // mobileMenu removed
        theme,
        setTheme,
        autoTheme,
        enableAutoTheme,
        // toast API
        toasts,
        addToast,
        removeToast,
      }}
    >
      {props.children}
    </DataContext.Provider>
  );
};
